import {
  type ComponentPropsWithRef,
  type ReactNode,
  type Ref,
  useState,
} from "react";

import { cn } from "../../utils";
import { Error as FieldError } from "./Error";
import { Label } from "./Label";

export interface ITextInputProps
  extends Omit<ComponentPropsWithRef<"input">, "size"> {
  label?: ReactNode;
  error?: string;
  textAppend?: ReactNode;
  autoselect?: ReactNode;
  confirmation?: string;
  /** Maps to `data-test-id` on the input (e.g. integration tests). */
  dataTestId?: string;
}

const TextInput = ({
  label,
  error,
  textAppend,
  autoselect,
  confirmation,
  id,
  className,
  onFocus,
  onBlur,
  disabled = false,
  type = "text",
  dataTestId,
  required,
  ref,
  ...rest
}: ITextInputProps & {
  ref?: Ref<HTMLInputElement>;
}) => {
  const [focused, setFocused] = useState(false);

  return (
    <div className={cn(disabled ? "opacity-60 select-none" : "", className)}>
      <div className="relative">
        {label && (
          <Label
            className="mb-2"
            htmlFor={id}
            disabled={disabled}
            required={required}
          >
            {label}
          </Label>
        )}
        <div
          className={cn(
            "relative flex flex-nowrap bg-white items-center overflow-hidden rounded-sm border font-semibold border-primary-100 transition-colors duration-300 ease-out",
            {
              "hover:border-primary/30": !error && !disabled && !focused,
              "border-primary": focused && !error,
              "border-red-500": !!error,
              "border-mint-500": !!confirmation && !error,
              "cursor-not-allowed shadow-none": disabled,
            },
          )}
        >
          {disabled && <div className="absolute inset-0 z-1" />}

          {/* typography: input value style — design-system primitive contract, not a heading */}
          <input
            ref={ref}
            id={id}
            type={type}
            disabled={disabled}
            className={cn(
              "flex w-full h-12 items-center outline-hidden shadow-none text-ellipsis border-0 px-4 py-0 bg-white text-black leading-6 appearance-none text-base font-semibold placeholder:text-gray-500 placeholder:font-normal",
              {
                "opacity-60 select-none text-black/50": disabled,
              },
            )}
            style={{ fontSize: "16px" }}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            {...(dataTestId ? { "data-test-id": dataTestId } : {})}
            aria-required={required || undefined}
            required={required}
            {...rest}
          />

          {textAppend}
        </div>
        {autoselect}
      </div>
      {error && <FieldError className="ml-1 mt-1">{error}</FieldError>}
      {confirmation && !error && (
        <div className="ml-1 mt-1 text-sm font-semibold text-mint-500">
          {confirmation}
        </div>
      )}
    </div>
  );
};

TextInput.displayName = "TextInput";

export { TextInput };
